import { List, message, Modal, Progress, Switch } from "antd";
import { StoredCustomerLicenses, UserLicense } from "../../Utils/types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import UserManagementFunction from "../../Functions/UserManagement/UserManagement";
import Loader from "../../Components/GlobalLoader/Loader";
import { useLicenseSettings } from "./LicenseSettingsContext";
import { handleTransferErrors } from "./utils/LicenseManipulation";

interface LicenseTransferModalProps {
  visible: boolean;
  setModalVisible: (visible: boolean) => void;
  transferToLicense: StoredCustomerLicenses;
  customerLicenseId: number;
  licenseName: string;
}

const LicenseTransferModal = (props: LicenseTransferModalProps) => {
  const sessionToken = useSelector((state: RootState) => state.user.token);
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [transfering, setTransfering] = useState(false);
  const { toggleVisible, forceUpdate } = useLicenseSettings();

  const [userLicenses, setUserLicenses] = useState<UserLicense[]>([]);
  const [newUserLicenses, setNewUserLicenses] = useState<UserLicense[]>([]);
  const [selectedLicenses, setSelectedLicenses] = useState<
    { licenseId: number; userId: number }[]
  >([]);

  message.config({
    maxCount: 1,
  });

  const [exceededLimit, setExceededLimit] = useState<boolean>(false);

  const isUserAlreadyActivated = (userId: number) => {
    return newUserLicenses.some((license) => license.userId === userId);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!accessToken || !sessionToken) return;
      setLoading(true);
      try {
        const [oldLicensesResponse, newLicensesResponse] = await Promise.all([
          UserManagementFunction.getActivatedLicenses(
            accessToken,
            sessionToken,
            props.customerLicenseId
          ),
          UserManagementFunction.getActivatedLicenses(
            accessToken,
            sessionToken,
            props.transferToLicense.key
          ),
        ]);

        const oldLicenses = oldLicensesResponse.data ?? [];
        const newLicenses = newLicensesResponse.data ?? [];

        setUserLicenses(oldLicenses);
        setNewUserLicenses(newLicenses);

        const availableLicenses =
          props.transferToLicense.value === "unlimited"
            ? Infinity
            : Number(props.transferToLicense.value) -
              Number(props.transferToLicense.usedValue);
        const filteredLicenses = oldLicenses.filter(
          (license: UserLicense) =>
            !newLicenses.some(
              (newLicense: UserLicense) => newLicense.userId === license.userId
            )
        );

        setExceededLimit(
          availableLicenses !== Infinity &&
            filteredLicenses.length > availableLicenses
        );

        if (
          availableLicenses !== Infinity &&
          filteredLicenses.length > availableLicenses
        ) {
          setSelectedLicenses([]);
        } else {
          setSelectedLicenses(
            filteredLicenses
              .slice(
                0,
                availableLicenses === Infinity ? undefined : availableLicenses
              )
              .map((license: UserLicense) => ({
                userId: license.userId,
                licenseId: license.id,
              }))
          );
        }
      } catch (error) {
        console.log(error);
        message.error("Failed to fetch user licenses");
      } finally {
        setLoading(false);
      }
    };

    if (props.visible) fetchData();
  }, [props.visible]);

  const handleToggle = (
    licenseId: number,
    userId: number,
    checked: boolean
  ) => {
    const availableLicenses =
      props.transferToLicense.value === "unlimited"
        ? Infinity
        : Number(props.transferToLicense.value) -
          Number(props.transferToLicense.usedValue);
    setSelectedLicenses((prev) => {
      if (checked && prev.length >= availableLicenses) {
        message.error(
          `You can only select up to ${Number(availableLicenses)} licenses.`
        );
        return prev;
      }
      return checked
        ? [...prev, { licenseId, userId }]
        : prev.filter((item) => item.userId !== userId);
    });
  };

  const handleTransfer = async (
    userLicenses: { userId: number; licenseId: number }[],
    newCustomerLicenseId: number,
    oldCustomerLicenseId: number
  ) => {
    setProgress(0);
    setTransfering(true);
    const isUcaas = props.licenseName.includes("UCaaS");
    const errors: string[] = [];
    const transferFunction = isUcaas
      ? UserManagementFunction.moveUserLicense
      : UserManagementFunction.activateUserLicense;

    const transferLicense = async (userId: number, licenseId: number) => {
      if (!accessToken || !sessionToken) return;
      try {
        const response = await transferFunction(accessToken!, sessionToken!, {
          userId,
          customerLicenseId: newCustomerLicenseId,
          customerLicenseIdNew: newCustomerLicenseId,
          customerLicenseIdOld: oldCustomerLicenseId,
        });

        if (response.status) {
          if (!isUcaas) {
            await UserManagementFunction.deactivateUserLicense(
              accessToken,
              sessionToken,
              { id: licenseId.toString() }
            );
          }
        } else {
          throw new Error(`Failed to transfer license for user ID: ${userId}`);
        }
      } catch (error) {
        errors.push(`Failed to transfer license for user ID: ${userId}`);
        message.error(`Failed to transfer license for user ID: ${userId}`);
      }
    };

    for (let i = 0; i < userLicenses.length; i++) {
      await transferLicense(userLicenses[i].userId, userLicenses[i].licenseId);
      setProgress(((i + 1) / userLicenses.length) * 100);
    }

    setTransfering(false);
    if (errors.length > 0) {
      const formattedErrors = handleTransferErrors(errors);
      message.error(
        <div>
          <p>Some licenses failed to transfer:</p>
          {formattedErrors.map((error) => (
            <p key={error.id}>{error.message}</p>
          ))}
        </div>,
        5
      );
      return;
    } else {
      message.success("All licenses transferred successfully");
    }
    props.setModalVisible(false);
    toggleVisible(false);
    forceUpdate();
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "18px", fontWeight: "bold" }}>
          Transfer License
        </span>
      }
      open={props.visible}
      onOk={() =>
        handleTransfer(
          selectedLicenses,
          props.transferToLicense.key,
          props.customerLicenseId
        )
      }
      onCancel={() => props.setModalVisible(false)}
      okText="Transfer"
      okButtonProps={{
        disabled: loading || transfering || selectedLicenses.length === 0,
      }}
      cancelText="Cancel"
      maskClosable={true}
      width={600}
    >
      <div
        style={{
          marginBottom: "16px",
          padding: "8px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
        }}
      >
        {exceededLimit ? (
          <p style={{ margin: 0, color: "#ff4d4f", fontWeight: "bold" }}>
            The amount of activated licenses exceeds the total amount of
            available licenses on this account. Please select the licenses you
            would like to transfer.
          </p>
        ) : (
          <p style={{ margin: 0, color: "#ff4d4f", fontWeight: "bold" }}>
            The following licenses will be transferred from this trial license
            to a full license of the same type. Please select the licenses you
            would like to transfer.
          </p>
        )}
      </div>
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {loading ? (
          <div>
            <Loader blue={true} size={"sm"} backgroundWeb={false} />
          </div>
        ) : (
          <List
            locale={{ emptyText: "No licenses found" }}
            dataSource={userLicenses}
            renderItem={(license) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <>
                      {license.firstName + " " + (license.lastName ?? "")}
                      {isUserAlreadyActivated(license.userId) && (
                        <span style={{ color: "red", marginLeft: "8px" }}>
                          (Already Activated)
                        </span>
                      )}
                    </>
                  }
                  description={`User ID: ${license.userId}`}
                />
                <Switch
                  checked={selectedLicenses.some(
                    (item) => item.userId === license.userId
                  )}
                  onChange={(checked) =>
                    handleToggle(license.id, license.userId, checked)
                  }
                  disabled={isUserAlreadyActivated(license.userId)}
                />
              </List.Item>
            )}
          />
        )}
      </div>
      {transfering && (
        <Progress
          percent={progress}
          status="active"
          showInfo={false}
          style={{ marginTop: "16px" }}
          strokeColor={{
            from: "#4C6798",
            to: "#00266c",
          }}
        />
      )}
    </Modal>
  );
};

export default LicenseTransferModal;
